import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import Heading from '@tds/core-heading';
import Modal from '@tds/community-modal';
import InputGroup from '@tds/community-input-group';

import ModalCustom from '../design/shared/modal';
import Table from '../design/shared/table';
import { LinkButton, ContentWrapper, Box } from '../design/css';
import UserForm from './form';
import request, { USER_RIGHTS } from '../utils/request';
import { setConstant } from '../utils/utils';
import ValidateLoader from '../UI/validateLoader';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { CONSTANT } from '../utils/constant';
import { BulkUploadBox, Search } from './css';
import { getUsersUrl } from './utils';
import MessageNotification from '../messageNotification';

const fetchUsers = url => {
  return request.get(url);
};

const fetchRoles = () => {
  return request.get('/roles');
};

const fetchChannels = () => {
  return request.get('/channels');
};

const fetchProvinces = () => {
  return request.get('/provinces');
};

const fetchDealership = () => {
  return request.get('/dealerships');
};

const Users = ({
  history,
  location: { search },
  match: { params },
  locale
}) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    adminLinks: { bulkUpload }
  } = localizedStrings;

  const TRANSLATION = localizedStrings.USERS.LIST;
  const title = TRANSLATION.TITLE;
  const {
    deleteMessage,
    declineMessage,
    approveMessage,
    updateMessage,
    actionButtons: {
      deleteButtonTitle,
      editButtonTitle,
      approveButtonTitle,
      declineButtonTitle
    },
    searchLabelTitle,
    searchHintTitle,
    userProfileModalTitle
  } = TRANSLATION;
  const tableHeaders = {
    first_name: TRANSLATION.COLUMNS.FIRST_NAME,
    last_name: TRANSLATION.COLUMNS.LAST_NAME,
    email: TRANSLATION.COLUMNS.EMAIL,
    role_description: TRANSLATION.COLUMNS.ROLE,
    actions: TRANSLATION.COLUMNS.ACTIONS
  };
  const ACTIONS = {
    DELETE: TRANSLATION.ACTIONS.DELETE,
    EDIT: TRANSLATION.ACTIONS.EDIT,
    APPROVE: TRANSLATION.ACTIONS.APPROVE,
    DECLINE: TRANSLATION.ACTIONS.DECLINE
  };
  const { page, q } = queryString.parse(search);
  let { userId } = params;
  const [isLoading, setIsLoading] = useState();
  const [users, setUsers] = useState();
  const focusElementOnClose = useRef(null);
  const [user, setUser] = useState();
  const [actionMessage, setActionMessage] = useState();
  const [roles, setRoles] = useState();
  const [channels, setChannels] = useState();
  const [action, setAction] = useState('');
  const [dialogUser, setDialogUser] = useState();
  const [showTelusModal, setShowTelusModal] = useState(false);
  const [searchString, setSearchString] = useState(q);
  const [provincesList, setProvincesList] = useState([]);
  const [dealershipList, setDealershipList] = useState([]);

  const handleUsers = ({ data }) => {
    const tempUsers = data.users.map(u => {
      return { ...u, role_description: u.role.description };
    });
    setUsers({ data: tempUsers, count: data.count });
    if (userId) {
      document.body.style.overflow = 'hidden';
      setUser(tempUsers.find(u => u.id === parseInt(userId, 10)));
    }
  };
  const getUsers = () => {
    setIsLoading(true);
    fetchUsers(getUsersUrl(page, searchString)).then(response => {
      handleUsers(response);
      setIsLoading(false);
      window.scrollTo(0, 0);
    });
  };

  const showBulkUploadButton = () => {
    const { getProfile } = request;
    const profile = getProfile()
      ? USER_RIGHTS[getProfile().role.name]
      : undefined;
    const { isBulkUploadVisible } = profile;
    return isBulkUploadVisible;
  };

  const onSearchClick = () => {
    history.push({
      search: `?q=${searchString}`
    });
  };
  const onClear = () => {
    setSearchString('');
    history.push({
      search: ''
    });
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchUsers(getUsersUrl(page, q)),
      fetchRoles(),
      fetchChannels(),
      fetchProvinces(),
      fetchDealership()
    ]).then(
      ([usersResp, rolesResp, channelsResp, provincesResp, dealershipResp]) => {
        handleUsers(usersResp);

        const apiRoles = rolesResp.data.roles.filter(item => {
          return (
            !['CORPORATE_STORE_REP', 'CORPORATE_STORE_MANAGER'].includes(
              item.name
            ) && item
          );
        });

        setRoles(apiRoles);
        setConstant('ROLES', apiRoles, 'name');
        setChannels(channelsResp.data.channels);
        setConstant('CHANNELS', channelsResp.data.channels, 'name');
        setProvincesList(provincesResp.data.provinces);
        setDealershipList(dealershipResp.data.rows);

        setIsLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    if (!user) {
      getUsers();
    }
  }, [page, q]);

  const editUser = row => {
    document.body.style.overflow = 'hidden';
    setUser(row);
    history.push(`/${locale}/users/${row.id}`);
  };

  const closeModal = msg => {
    document.body.style.overflow = 'unset';
    setUser();
    setActionMessage(msg);
    setTimeout(() => setActionMessage(null), 3000); // remove update message after 3 seconds
    history.goBack();
  };

  const openTelusModal = () => {
    setShowTelusModal(true);
  };

  const closeTelusModal = () => {
    setShowTelusModal(false);
  };

  const successActionHandling = () => {
    getUsers();
    setDialogUser();
    setAction('');
    closeTelusModal();
  };

  const successfulUpdate = () => {
    userId = undefined;
    closeModal(updateMessage);
    getUsers();
  };

  const approveUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.APPROVE);
    openTelusModal();
  };

  const declineUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.DECLINE);
    openTelusModal();
  };

  const deleteUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.DELETE);
    openTelusModal();
  };

  const proceed = () => {
    switch (action) {
      case ACTIONS.DELETE:
        request.post(`/users/${dialogUser.id}/delete`, dialogUser).then(() => {
          setActionMessage(deleteMessage);
          successActionHandling();
        });
        break;
      case ACTIONS.DECLINE:
        request.post(`/users/${dialogUser.id}/delete`, dialogUser).then(() => {
          setActionMessage(declineMessage);
          successActionHandling();
        });
        break;
      case ACTIONS.APPROVE:
        request.post(`/users/${dialogUser.id}/approve`, dialogUser).then(() => {
          setActionMessage(approveMessage);
          successActionHandling();
        });
        break;
      default:
    }
  };

  const actions = row => {
    return (
      <>
        <LinkButton onClick={() => editUser(row)}>{editButtonTitle}</LinkButton>
        |
        <LinkButton onClick={() => deleteUser(row)}>
          {deleteButtonTitle}
        </LinkButton>
        {row.status === CONSTANT.KEYS.USER_STATUS.PENDING_APPROVAL && (
          <>
            |
            <LinkButton onClick={() => approveUser(row)}>
              {approveButtonTitle}
            </LinkButton>
            |
            <LinkButton onClick={() => declineUser(row)}>
              {declineButtonTitle}
            </LinkButton>
          </>
        )}
      </>
    );
  };

  return (
    <ContentWrapper>
      {isLoading ? (
        <ValidateLoader message="Loading" />
      ) : (
        <>
          {dialogUser && (
            <Modal
              heading={TRANSLATION.confirmationDialog.HEADING}
              bodyText={`${
                TRANSLATION.confirmationDialog.PRE_TEXT
              } ${TRANSLATION.ACTIONS[action.toUpperCase()].toUpperCase()} ${
                dialogUser.first_name
              } ${dialogUser.last_name} ${dialogUser.email}?`}
              confirmCTAText={TRANSLATION.confirmationDialog.CONFIRM}
              cancelCTAText={TRANSLATION.confirmationDialog.CANCEL}
              onConfirm={proceed}
              focusElementAfterClose={focusElementOnClose}
              onClose={closeTelusModal}
              isOpen={showTelusModal}
            />
          )}
          <Search>
            <BulkUploadBox>
              {showBulkUploadButton() && (
                <Link variant="link" to="/bulk-upload">
                  <span>
                    <button type="button" className="frg-button color-purple">
                      {bulkUpload}
                    </button>
                  </span>
                </Link>
              )}
            </BulkUploadBox>
            <InputGroup
              label={searchLabelTitle}
              hint={searchHintTitle}
              defaultValue={searchString}
              onClearButtonClick={() => onClear()}
              onChange={e => setSearchString(e.target.value)}
              onButtonClick={() => onSearchClick()}
              onKeyDown={e => e.key === 'Enter' && onSearchClick()}
            />
          </Search>
          {actionMessage && (
            <Box pt={20} pb={20}>
              <MessageNotification variant="success" message={actionMessage} />
            </Box>
          )}
          {users && users.data && users.data.length ? (
            <Table
              title={title}
              header={tableHeaders}
              data={users.data}
              actions={actions}
              currentPage={parseInt(page || 1, 10)}
              count={users.count}
              url={searchString && `?q=${searchString}`}
            />
          ) : (
            <Heading level="h3">{TRANSLATION.NO_RECORDS}</Heading>
          )}
          {user && roles && channels && (
            <ModalCustom
              open={!!user}
              setOpen={closeModal}
              header={userProfileModalTitle}
            >
              <UserForm
                user={user}
                roles={roles}
                channels={channels}
                provinces={provincesList}
                dealershipList={dealershipList}
                submissionHandling={successfulUpdate}
              />
            </ModalCustom>
          )}
        </>
      )}
    </ContentWrapper>
  );
};

Users.propTypes = {
  locale: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    goBack: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object
  }).isRequired
};

export default Users;
